import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Autocomplete,
  Alert,
  CircularProgress,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Business as ProjectIcon,
  Apartment as BuildingIcon,
  Event as DateIcon,
  FormatListBulleted as AmenitiesIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { getProjects } from '../../../services/project';
import { showSnackbar } from '../../../components/Snackbar';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const BuildingForm = () => {
  const history = useHistory();
  const location = useLocation();
  const buildingData = location.state?.building;
  const isEditMode = !!buildingData;

  const urlParams = new URLSearchParams(location.search);
  const urlProjectId = urlParams.get('projectId');

  const isPreSelectedProject = !!(buildingData?.projectId || urlProjectId);


  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [error, setError] = useState('');
  const [amenitiesMaster, setAmenitiesMaster] = useState([]);

  const [formData, setFormData] = useState({
    id: buildingData?.id || '',
    name: buildingData?.name || '',
    project: buildingData?.project || '',
    projectId: buildingData?.projectId || urlProjectId || '',
    projectType: buildingData?.projectType || 'Apartment',
    status: buildingData?.status || 'available',
    totalFloors: buildingData?.totalFloors || '',
    totalFlats: buildingData?.totalFlats || '',
    startDate: buildingData?.startDate || '',
    completionDate: buildingData?.completionDate || '',
    amenities: buildingData?.amenities || [],
    remark: buildingData?.remark || '',
  });

  useEffect(() => {
    fetchProjects();
    fetchAmenities();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const data = await getProjects({ projectType: formData.projectType });
      const projectsList = data || [];
      setProjects(projectsList);

      // Auto-select if only one project exists and no project is currently selected
      if (projectsList.length === 1 && !formData.projectId) {
        const singleProject = projectsList[0];
        setFormData(prev => ({ ...prev, projectId: singleProject._id || singleProject.id, project: singleProject.projectName || singleProject.name }));
      } else if (formData.projectId && !formData.project && projectsList) {
        // If we have a project ID but no project name (e.g. from URL), find it
        const proj = projectsList.find(p => p._id === formData.projectId);
        if (proj) {
          setFormData(prev => ({ ...prev, project: proj.projectName || proj.name }));
        }
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails?type=amenities`);
      if (!response.ok) return;
      const result = await response.json();
      const list = result?.data || result?.details || result || [];

      const normalized = list
        .map((item) => {
          if (!item) return null;
          const id = item._id || item.id;
          const label = item?.details?.amenitiesName || item?.details?.name || item?.amenitiesName || item?.name || item?.value || item?.label;
          if (id && label) return { id, label };
          return null;
        })
        .filter((v) => v && v.id && v.label);

      setAmenitiesMaster(normalized);
    } catch (err) {
      console.error('Error fetching amenities:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProjectChange = (event, newValue) => {
    if (newValue) {
      setFormData((prev) => ({
        ...prev,
        project: newValue.projectName || newValue.name,
        projectId: newValue._id,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        project: '',
        projectId: '',
      }));
    }
  };

  const handleAmenityChange = (event) => {
    const {
      target: { value },
    } = event;
    setFormData((prev) => ({
      ...prev,
      amenities: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.projectId || !formData.status) {
      setError('Please fill in all required fields (Building Name, Project, Status)');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const amenityNames = (formData.amenities || [])
        .map((item) => (item && typeof item === 'object' ? item.label || item.name || item.value : item))
        .filter(Boolean);

      const payload = {
        buildingName: formData.name,
        projectId: formData.projectId,
        inventoryType: formData.projectType || 'Apartment',
        projectType: formData.projectType || 'Apartment',
        status: (formData.status || '').toLowerCase(),
        totalFloors: Number(formData.totalFloors) || 0,
        totalFlats: Number(formData.totalFlats) || 0,
        startDate: formData.startDate,
        completionDate: formData.completionDate,
        amenities: amenityNames,
        remark: formData.remark,
        updatedBy: "Admin"
      };

      let response;
      if (isEditMode) {
        response = await fetch(`${API_BASE}/api/projects/inventory/${formData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/api/projects/inventory/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      const body = await response.json().catch(() => ({}));
      if (response.ok || body.status || body.success) {
        showSnackbar({
          type: 'success',
          message: isEditMode ? 'Building updated successfully!' : 'Building added successfully!'
        });
        history.push('/app/project/apartment/buildings', { projectId: formData.projectId });
      } else {
        throw new Error(body.message || 'Operation failed');
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => history.goBack()} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a203e', mb: 0.5 }}>
              {isEditMode ? 'Edit Building' : 'Add New Building'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEditMode ? `Updating building details for ${formData.name}` : 'Create a new building structure within your project'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        {/* Project Assignment Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ProjectIcon color="primary" /> Project Assignment
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={projects}
              getOptionLabel={(option) => option.projectName || option.name || ''}
              value={projects.find(p => p._id === formData.projectId) || null}
              onChange={handleProjectChange}
              disabled={isEditMode || isPreSelectedProject}
              loading={loadingProjects}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Project"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loadingProjects ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Usage / Property Type</InputLabel>
              <Select
                name="projectType"
                value={formData.projectType}
                onChange={handleInputChange}
                label="Usage / Property Type"
              >
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                label="Status"
              >
                <MenuItem value="available">Available</MenuItem>
                <MenuItem value="under_construction">Under Construction</MenuItem>
                <MenuItem value="hold">Hold</MenuItem>
                <MenuItem value="booked">Fully Booked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Building Details Section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <BuildingIcon color="primary" /> Building Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Building Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="e.g. Tower A, Block 1"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Total Floors"
              name="totalFloors"
              type="number"
              value={formData.totalFloors}
              onChange={handleInputChange}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Total Flats"
              name="totalFlats"
              type="number"
              value={formData.totalFlats}
              onChange={handleInputChange}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>

        {/* Amenities section */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AmenitiesIcon color="primary" /> Amenities & Facilities
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Amenities</InputLabel>
              <Select
                multiple
                value={formData.amenities}
                onChange={handleAmenityChange}
                input={<OutlinedInput label="Select Amenities" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              >
                {amenitiesMaster.map((amenity) => (
                  <MenuItem key={amenity.id} value={amenity.label}>
                    <Checkbox checked={formData.amenities.indexOf(amenity.label) > -1} />
                    <ListItemText primary={amenity.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Timeline & Remarks */}
        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <DateIcon color="primary" /> Project Timeline & Notes
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Completion Date"
              name="completionDate"
              type="date"
              value={formData.completionDate}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name="remark"
              label="Additional Remarks"
              value={formData.remark}
              onChange={handleInputChange}
              multiline
              rows={3}
              placeholder="Any special notes or details..."
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mt: 5, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            onClick={() => history.goBack()}
            sx={{ px: 4, py: 1.5, borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 8px 16px rgba(55, 125, 255, 0.24)',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Building' : 'Save Building'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default BuildingForm;
